import { LAYER_TYPE } from '@tmapy/config';
import { INTERACTION_DRAW_FEATURES, SET_MAP_DEFAULT_STATE, SET_MAP_STATE, SET_LAYER_VISIBILITY, SET_LAYER_OPACITY, SET_LAYER_FEATURES, SET_VECTOR_TILE_HIDDEN_FEATURES, DELETE_ALL_FEATURES, INTERACTION_MODIFY_FEATURES, INVALIDATE_VECTOR_TILE_LAYER, DELETE_FEATURES, MERGE_LAYERS, SET_LAYER_FEATURE_HIGHLIGHT, SET_LAYER_STYLE, SET_LAYER_MAPBOX_STYLE, ADD_VECTOR_TILE_HIDDEN_FEATURES, ADD_LAYERS, } from '../constants';
import { getInitialState } from './initialState';
const setVisibilityByOpacity = (layer) => ({
    ...layer,
    layerOptions: {
        ...layer.layerOptions,
        visible: layer.layerOptions.visible && layer.layerOptions.opacity !== 0,
    },
});
const EMPTY_ARRAY = [];
export const layers = (state = getInitialState().layers, action) => {
    switch (action.type) {
        case ADD_LAYERS: {
            const startZIndex = state.length;
            return [
                ...state,
                ...action.payload.map((layer, idx) => {
                    const mutableLayer = { ...layer };
                    mutableLayer.label = undefined;
                    return {
                        ...mutableLayer,
                        layerOptions: { ...mutableLayer.layerOptions, zIndex: startZIndex + idx },
                    };
                }),
            ];
        }
        case SET_MAP_DEFAULT_STATE: {
            return getInitialState().layers.map(setVisibilityByOpacity);
        }
        case SET_MAP_STATE: {
            return action.payload.layers.map(setVisibilityByOpacity);
        }
        case SET_LAYER_STYLE: {
            return state.map((layer) => {
                if ((layer.type === LAYER_TYPE.TILE_VECTOR || layer.type === LAYER_TYPE.VECTOR) &&
                    layer.id === action.payload.id) {
                    return {
                        ...layer,
                        style: action.payload.style,
                        highlightStyle: action.payload.highlightStyle,
                    };
                }
                return layer;
            });
        }
        case SET_LAYER_MAPBOX_STYLE: {
            return state.map((layer) => {
                if ((layer.type === LAYER_TYPE.TILE_VECTOR || layer.type === LAYER_TYPE.VECTOR) &&
                    layer.id === action.payload.id) {
                    return {
                        ...layer,
                        mapboxStyle: action.payload.style,
                    };
                }
                return layer;
            });
        }
        case SET_LAYER_VISIBILITY: {
            return state.map((layer) => {
                const newLayerVisibility = action.payload[layer.id];
                if (newLayerVisibility !== undefined) {
                    return {
                        ...layer,
                        layerOptions: {
                            ...layer.layerOptions,
                            visible: newLayerVisibility,
                        },
                    };
                }
                return layer;
            });
        }
        case SET_LAYER_OPACITY: {
            return state.map((layer) => {
                const opacity = action.payload[layer.id];
                return opacity !== undefined
                    ? {
                        ...layer,
                        layerOptions: {
                            ...layer.layerOptions,
                            opacity,
                        },
                    }
                    : layer;
            });
        }
        case SET_LAYER_FEATURES: {
            return state.map((layer) => layer.id === action.payload.layerId
                ? { ...layer, features: action.payload.features }
                : layer);
        }
        case INTERACTION_DRAW_FEATURES: {
            return state.map((layer) => {
                if (layer.type !== LAYER_TYPE.VECTOR || layer.id !== action.payload.layerId)
                    return layer;
                const featureIdMap = new Map();
                for (const feature of action.payload.features) {
                    if (!feature.properties?.id) {
                        throw new Error(`Feature has no ID`);
                    }
                    featureIdMap.set(feature.properties.id, feature);
                }
                const features = layer.features.map((feature) => {
                    const id = feature.properties.id;
                    const newFeature = featureIdMap.get(id);
                    if (newFeature) {
                        featureIdMap.delete(id);
                        return newFeature;
                    }
                    return feature;
                });
                return {
                    ...layer,
                    features: featureIdMap.size
                        ? [...features, ...Array.from(featureIdMap.values())]
                        : features,
                };
            });
        }
        case DELETE_ALL_FEATURES: {
            return state.map((layer) => layer.id === action.payload.layerId ? { ...layer, features: EMPTY_ARRAY } : layer);
        }
        case DELETE_FEATURES: {
            return state.map((layer) => layer.type === LAYER_TYPE.VECTOR && layer.id === action.payload.layerId
                ? {
                    ...layer,
                    features: layer.features.filter((feature) => {
                        return !action.payload.featureIds.find((featureId) => featureId === feature.properties?.id);
                    }),
                }
                : layer);
        }
        case INTERACTION_MODIFY_FEATURES: {
            return state.map((layer) => layer.type === LAYER_TYPE.VECTOR && layer.id === action.payload.layerId
                ? {
                    ...layer,
                    features: layer.features.map((layerFeature) => {
                        const modifiedFeature = action.payload.features.find((payloadFeature) => payloadFeature.properties?.id === layerFeature.properties?.id);
                        return modifiedFeature ?? layerFeature;
                    }),
                }
                : layer);
        }
        case INVALIDATE_VECTOR_TILE_LAYER: {
            return state.map((layer) => layer.type === LAYER_TYPE.TILE_VECTOR && layer.id === action.payload.layerId
                ? { ...layer, version: (layer.version ?? 0) + 1 }
                : layer);
        }
        case SET_LAYER_FEATURE_HIGHLIGHT: {
            return state.map((layer) => {
                const featureIds = [];
                action.payload.highlightedFeatures.forEach((highlightedFeature) => {
                    if (highlightedFeature.layerId === layer.id && highlightedFeature.featureId) {
                        featureIds.push(highlightedFeature.featureId);
                    }
                });
                return layer.type === LAYER_TYPE.TILE_VECTOR || layer.type === LAYER_TYPE.VECTOR
                    ? {
                        ...layer,
                        highlightedFeatureIds: featureIds,
                    }
                    : layer;
            });
        }
        case SET_VECTOR_TILE_HIDDEN_FEATURES: {
            return state.map((layer) => layer.type === LAYER_TYPE.TILE_VECTOR && layer.id === action.payload.layerId
                ? { ...layer, hiddenFeatureIds: action.payload.featureIds }
                : layer);
        }
        case ADD_VECTOR_TILE_HIDDEN_FEATURES: {
            return state.map((layer) => layer.type === LAYER_TYPE.TILE_VECTOR && layer.id === action.payload.layerId
                ? {
                    ...layer,
                    hiddenFeatureIds: [...layer.hiddenFeatureIds, ...action.payload.featureIds],
                }
                : layer);
        }
        case MERGE_LAYERS: {
            const { toLayerId, fromLayerId } = action.payload;
            console.assert(toLayerId !== fromLayerId);
            return state.map((layer) => {
                if (layer.id === toLayerId) {
                    // TODO, remove duplicates
                    const features = [
                        ...layer.features,
                        ...state.find((fromLayer) => fromLayer.id === fromLayerId).features.map((feature) => ({
                            ...feature,
                            properties: {
                                ...feature.properties,
                                layerId: toLayerId,
                            },
                        })),
                    ];
                    return {
                        ...layer,
                        features,
                    };
                }
                if (layer.id === fromLayerId) {
                    return {
                        ...layer,
                        features: EMPTY_ARRAY,
                    };
                }
                return layer;
            });
        }
        default: {
            return state;
        }
    }
};
//# sourceMappingURL=layersReducer.js.map